'use client';

import clsx from 'clsx';
import { usePathname } from 'next/navigation';
import { useEffect, useState } from 'react';

import '../freestar-cls.css';
import { isRegisteredAdFreePath } from '../utils/ad-free-registry';
import { PLACEMENTS_HASH } from '../utils/constants';
import { isWebview } from '../utils/helpers';
import { FreestarPlacementId } from '../utils/types';

interface AdPlaceholderProps {
	placementId: FreestarPlacementId;
	place?: number;
	showFromWidth?: number;
	hideFromWidth?: number;
	containerClassname?: string;
}

/**
 * DEV/TEST stand-in for a Freestar ad slot. Freestar is fully disabled outside prod
 * (see HeaderScripts / AdShieldScripts), so nothing would render here otherwise.
 *
 * The `placementId` class comes from freestar-cls.css and is what FreestarAdSlot applies in prod
 * via `classList`. It supplies BOTH the min-height reservation and the flex centering, so non-prod
 * spacing stays identical to prod and we add no layout CSS of our own.
 *
 * NOTE: FreestarAdSlot renders an extra bare <div> between the flex wrapper and the classed div.
 * We deliberately omit it — it is shrink-to-fit, so `width: 100%` on the classed div would resolve
 * against it while our `w-full` box resolves against the classed div, collapsing both to 0.
 */
export const AdPlaceholder = ({ placementId, place, showFromWidth, hideFromWidth, containerClassname }: AdPlaceholderProps) => {
	const [shouldRender, setShouldRender] = useState(false);
	// Same reason as CustomFreestarAd — see ad-free-registry.
	const pathname = usePathname();

	const slotId = place !== undefined ? `${placementId}_${place}` : placementId;
	const { width, height } = PLACEMENTS_HASH[placementId].placeholder;

	/*
	 * min-h-0 AND min-w-0 are both required. This is a flex item, so its automatic minimum size is its
	 * min-content size. Two things leak a fixed px size into that minimum: a definite `width`, and an
	 * `aspect-ratio` paired with a definite `max-height` (which transfers the height back through the
	 * ratio into a width — 90px * 728/90 = 728px). Either one makes any shrink-wrapping ancestor size
	 * itself to 728px and overflow a phone viewport. Width is therefore percentage-only (`w-full`)
	 * with the px cap expressed as `max-width`, which creates no intrinsic minimum.
	 * overflow-hidden clips the label rather than growing the box; mx-auto centres it.
	 */
	const boxClasses = clsx(
		'min-h-0 min-w-0 mx-auto w-full flex flex-col items-center justify-center gap-0.5 overflow-hidden',
		'rounded-md border border-dashed border-gray-300 bg-gray-200'
	);

	// Same visibility gates as CustomFreestarAd so the DEV/TEST layout matches prod.
	useEffect(() => {
		if (!isWebview() && (!showFromWidth || showFromWidth <= window.innerWidth) && (!hideFromWidth || window.innerWidth < hideFromWidth)) {
			setShouldRender(true);
		}
	}, []);

	if (isRegisteredAdFreePath(pathname)) return null;

	if (!shouldRender) return null;

	return (
		<div className={clsx('w-full', containerClassname)}>
			<div className={clsx(placementId, 'w-full')} id={slotId}>
				{/*
					No `justify-center` on the wrapper and no definite width below it. A caller's
					containerClassname may contain `grid`, which overrides our display mode (Tailwind emits
					`.grid` after `.flex`, so attribute order does not decide it). `justify-center` on a grid
					shrink-wraps the implicit track instead of centring inside it, and the track then takes the
					box's intrinsic width — collapsing to the label text on desktop, or blowing past the
					viewport on mobile once a px width is involved. With justify-content left at its default the
					track stretches to the container, so `w-full` has a definite basis in both display modes.
					See apps/pickleball LeaguesList.tsx for a call site that passes `grid`.
				*/}
				<div className={boxClasses} style={{ maxWidth: width, aspectRatio: `${width} / ${height}` }}>
					<span className="text-[11px] font-light leading-tight tracking-widest text-gray-700">ADVERTISEMENT</span>
					<span className="px-2 text-center text-[10px] leading-tight text-gray-500 break-all">{slotId}</span>
				</div>
			</div>
		</div>
	);
};
